// ! There is lots of documentation about i18next and our approach to it in Notion
// https://www.notion.so/maul/Translations-for-web-3e1c19c3d3f14b85840af73dc5b3a55e

import type { InitOptions, i18n } from "i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import Locize from "i18next-locize-backend"
import { Settings } from "luxon"
import { initReactI18next } from "react-i18next"
import type { TI18nLang } from "../shared/types"
import { getClientEnv } from "./client-env-init"
import { fspConfig } from "./fsp-config"

export type TI18nSetup = {
  attachEventListeners: (
    i18n: i18n,

    luxonSettings: typeof Settings,
  ) => void
  pickFallbackLng: (hostname: string) => TI18nLang
  createServerOptions(lng: TI18nLang, fallbackLng: TI18nLang): InitOptions
  createClientOptions: (fallbackLng: TI18nLang) => InitOptions
  useIsomorphicPlugins: (i18n: i18n) => i18n
  useClientPlugins: (i18n: i18n) => i18n
  useAllPlugins: (i18n: i18n) => i18n
}

// TODO We might want useServerSidePlugins, useClientSidePlugins, and useAllPlugins.
// Similar for event listeners.
// Depending on what the hydrations mismatch is, which I don't remember.
export const I18nSetup: TI18nSetup = {
  attachEventListeners,
  createServerOptions,
  createClientOptions,
  pickFallbackLng,
  useIsomorphicPlugins,
  useClientPlugins,
  useAllPlugins,
}

function attachEventListeners(i18n: i18n, luxonSettings: typeof Settings): void {
  const func = "attachEventListeners"
  console.log("🌐", { func })

  // Note that we must define the listeners before the configuration so the listeners are already
  // set up when the configuration fires events.

  const EVENT = {
    initialized: "initialized",
    languageChanged: "languageChanged",
    loaded: "loaded",
  }

  // https://www.i18next.com/overview/api#onlanguagechanged
  i18n.on(EVENT.initialized, (options) => {
    const func = `i18next.on('${EVENT.initialized}')`
    console.log(
      "🌐",
      { func },
      `Initializing, i18next got '${EVENT.initialized}' event with payload`,
      { options },
    )
  })

  i18n.on(EVENT.loaded, (loaded) => {
    const func = `i18next.on('${EVENT.loaded}')`
    console.log("🌐", { func }, `i18next got '${EVENT.loaded}' event with payload`, {
      loaded,
    })
  })

  i18n.on(EVENT.languageChanged, (lng) => {
    const func = `i18next.on('${EVENT.languageChanged}')`
    console.log(
      "🌐",
      { func },
      `i18next got '${EVENT.languageChanged}' event with payload '${lng}'.\n`,
      `Setting Amplify language to '${lng}'.\n`,
      `Setting Luxon default locale to '${lng}'.`,
    )

    luxonSettings.defaultLocale = lng
  })
}

function pickFallbackLng(hostname: string): TI18nLang {
  const func = "pickFallbackLng"

  // TODO This will cause problems when doing SSR
  // And currently for Vercel domains?? The config below doesn't take Vercel into account.

  // TODO This should probably be in initDomainConfiguration, otherwise we have to keep the two objects
  // keyed by domain name in sync
  const fallbackLngByHostname: Record<string, TI18nLang> = {
    localhost: "is",
    "restaurant.maul.is": "is",
    "kitchen.tiffin.dk": "da",
    "kitchen-web.vercel.app": "is",
    "https://kitchen-web-git-development-maul.vercel.app": "is",
  }
  const domainDefaultFallbackLng = fallbackLngByHostname[hostname]

  // TODO We should skip the fallback and rather throw an error if the hostname is not explicitly configured
  const fallbackLng: TI18nLang = domainDefaultFallbackLng ?? "en-GB"
  console.log(
    "🌐",
    { func },
    `Since the domain is ${hostname}, we're setting i18next fallback language to '${fallbackLng}'`,
  )

  return fallbackLng
}

function createBaseOptions(fallbackLng: TI18nLang): Partial<InitOptions> {
  const options: InitOptions = {
    //resources,
    fallbackLng,
    ns: ["common", fspConfig.ns],
    // TODO Set defaultNS: false to force specifying namespace every time
    defaultNS: "common", // TODO what should the default namespace be?
    keySeparator: ".",
    // TODO Will this be a string at runtime so we need to coerce to boolean?
    // Add a parsed debug setting to clientEnvSchema before enabling this.
    interpolation: {
      escapeValue: false, // react already safes us from xss
    },
    backend: {
      projectId: getClientEnv().VITE_LOCIZE_PROJECT_ID,
      // We only need to set apiKey and referenceLng to use saveMissing feature of i18next, see
      // https://github.com/locize/i18next-locize-backend
      apiKey: getClientEnv().VITE_LOCIZE_API_KEY,
      referenceLng: "en",
      version: getClientEnv().VITE_LOCIZE_PROJECT_VERSION, // defaults to latest
      cdnType: "pro",
    },
  }

  return options
}

function createServerOptions(lng: TI18nLang, fallbackLng: TI18nLang): InitOptions {
  const func = "createServerOptions"

  const baseOptions = createBaseOptions(fallbackLng)
  const serverOnlyOptions = { lng }

  const options: InitOptions = {
    ...serverOnlyOptions,
    ...baseOptions,
  }

  return options
}

function createClientOptions(fallbackLng: TI18nLang): InitOptions {
  const func = "createClientOptions"

  const baseOptions = createBaseOptions(fallbackLng)
  const clientOnlyOptions: Partial<InitOptions> = {
    detection: {
      order: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
      caches: ["localStorage"],
    },
    react: {
      bindI18n: "languageChanged editorSaved",
    }, // This is from the locize github issue: https://github.com/locize/locize/issues/167
  }

  const options: InitOptions = {
    ...baseOptions,
    ...clientOnlyOptions,
  }

  return options
}

// TODO Maybe this function is not needed anymore
function useAllPlugins(i18n: i18n): i18n {
  const func = "useAllPlugins"

  const i18nWithAllPlugins = useIsomorphicPlugins(useClientPlugins(i18n))
  return i18nWithAllPlugins
}

function useIsomorphicPlugins(i18n: i18n): i18n {
  const func = "useIsomorphicPlugins"

  const i18nWithPlugins = i18n
    // https://github.com/locize/i18next-locize-backend
    // This is an i18next backend plugin to be used for locize service. It will load resources from locize server using http fetch or xhr as fallback.
    .use(Locize)

  return i18nWithPlugins
}

function useClientPlugins(i18n: i18n): i18n {
  const func = "useClientPlugins"

  const i18nWithPlugins = i18n
    // https://github.com/locize/locize
    // This plugin enables the incontext editing feature provided by locize.
    // https://github.com/i18next/i18next-browser-languageDetector
    // This is an i18next language detection plugin used to detect user language in the browser, supports localStorage, path, and more.
    .use(LanguageDetector)
    // passes i18n down to react-i18next
    .use(initReactI18next)

  return i18nWithPlugins
}
